import { Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api-client';
import { useAssignExitIp, useExitIps, useReleaseExitIp } from '@/hooks/use-exit-ips';

/**
 * Exit-IP pool editor for one server.
 *
 * Lists the server's assigned exit IPs and lets an operator add or remove
 * them. The backend rejects an IP already in use anywhere in the fleet with
 * a 409 — that message is surfaced inline under the add form.
 */
export function ExitIpEditor({ serverName }: { serverName: string }): JSX.Element {
  const list = useExitIps(serverName);
  const assign = useAssignExitIp(serverName);
  const release = useReleaseExitIp(serverName);

  const [ip, setIp] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleAdd(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    assign.mutate(
      { ip: ip.trim() },
      {
        onSuccess: (created) => {
          toast.success(`Exit IP ${created.ip} assigned.`);
          setIp('');
        },
        onError: (err) => {
          // 409 = the IP is already assigned somewhere in the fleet.
          setError(
            err instanceof ApiError && err.status === 409
              ? err.message
              : err instanceof ApiError
                ? err.message
                : 'Could not assign the IP.',
          );
        },
      },
    );
  }

  function handleRemove(target: string): void {
    release.mutate(target, {
      onSuccess: () => toast.success(`Exit IP ${target} released.`),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not release the IP.'),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Exit IPs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {list.isError && (
          <p className="text-sm text-destructive">Couldn't load exit IPs.</p>
        )}

        {list.data && list.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No exit IPs assigned.</p>
        )}

        {list.data && list.data.length > 0 && (
          <ul className="space-y-1 text-sm">
            {list.data.map((entry) => (
              <li key={entry.ip} className="flex items-center justify-between">
                <span className="font-mono">{entry.ip}</span>
                <div className="flex items-center gap-2">
                  {entry.mode && (
                    <span className="text-xs text-muted-foreground">{entry.mode}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(entry.ip)}
                    disabled={release.isPending}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Release ${entry.ip}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleAdd} className="space-y-2 border-t pt-3">
          <div className="flex gap-2">
            <Input
              value={ip}
              onChange={(event) => {
                setIp(event.target.value);
                setError(null);
              }}
              placeholder="Add exit IP (e.g. 203.0.113.50)"
              disabled={assign.isPending}
            />
            <Button type="submit" disabled={assign.isPending || ip.trim() === ''}>
              {assign.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
